import {
  spider,
  SUIT_SYMBOLS,
  type Card,
  type SpiderMoveDest,
  type SpiderMoveSource,
  type SpiderView,
} from '@cardengine/shared';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store.js';
import { PlayingCard } from './PlayingCard.js';
import { useSpiderFlights } from './useSpiderFlights.js';

/** Two sources are the same pick-up if they name the same cards. */
function sameSource(a: SpiderMoveSource, b: SpiderMoveSource): boolean {
  return a.index === b.index && a.depth === b.depth;
}

/**
 * Where a card sits down its column.
 *
 * Face-down cards stack tightly and face-up ones spread out, so the two
 * spacings are composed rather than multiplied against a shared row number —
 * the same arithmetic Klondike needs, and for the same reason. Spider leans on
 * it harder: a column can run past twenty cards deep.
 */
function offsetFor(downCount: number, upIndex: number | null): string {
  if (upIndex === null) return `calc(${downCount} * var(--stack-step-down))`;
  return `calc(${downCount} * var(--stack-step-down) + ${upIndex} * var(--stack-step))`;
}

/**
 * What the player has hold of.
 *
 * `liftable` records whether the engine would actually allow the pick-up, which
 * is what decides between a move and a bounce. It matters more in Spider than
 * anywhere else: a sequence that is plainly in order will still refuse to move
 * if it is not also in one suit, and being told so is the whole difference
 * between learning the game and being baffled by it.
 */
type Grip =
  | { kind: 'cards'; from: SpiderMoveSource; cards: Card[]; liftable: boolean; key: string }
  | { kind: 'faceDown'; key: string };

/** Matches the CSS bounce. */
const BOUNCE_MS = 420;

export function SpiderBoard() {
  const view = useStore((s) => s.spider);
  const move = useStore((s) => s.spiderMove);
  const dealRow = useStore((s) => s.spiderDeal);
  const pushToast = useStore((s) => s.pushToast);
  const highlightPlayable = useStore((s) => s.highlightPlayable);
  const setHighlightPlayable = useStore((s) => s.setHighlightPlayable);

  const [grip, setGrip] = useState<Grip | null>(null);
  const [bouncing, setBouncing] = useState<string | null>(null);
  const bounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const boardRef = useRef<HTMLDivElement | null>(null);
  const spiderEvents = useStore((s) => s.spiderEvents);
  // Dealing, and runs leaving the table. Measured from the live DOM, so it
  // needs the board's own element rather than anything computed here.
  const { flights, hiddenSlots, hiddenHome } = useSpiderFlights(view, spiderEvents, boardRef);

  // The same sensors as the other three tables, so a tap still reads as a click
  // and a drag needs a deliberate pull.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const moveNumber = view?.moveNumber ?? 0;
  useEffect(() => setGrip(null), [moveNumber]);

  useEffect(
    () => () => {
      if (bounceTimer.current) clearTimeout(bounceTimer.current);
    },
    [],
  );

  if (!view) return <p className="game__loading">Dealing…</p>;

  const moves = view.legalMoves;
  const canLift = (from: SpiderMoveSource) =>
    moves.some((m) => m.kind === 'move' && sameSource(m.from, from));

  /** Only a genuinely liftable grip lights anything up. */
  const heldSource = grip?.kind === 'cards' && grip.liftable ? grip.from : null;
  const canDrop = (to: SpiderMoveDest) =>
    heldSource !== null &&
    moves.some((m) => m.kind === 'move' && sameSource(m.from, heldSource) && m.to.index === to.index);

  const liftableCount = new Set(
    moves.filter((m) => m.kind === 'move').map((m) => JSON.stringify(m.from)),
  ).size;

  function refuse(key: string, message: string): void {
    pushToast(message, 'warn');
    if (bounceTimer.current) clearTimeout(bounceTimer.current);
    setBouncing(key);
    bounceTimer.current = setTimeout(() => setBouncing(null), BOUNCE_MS);
    setGrip(null);
  }

  /** Settle a grip against a destination: move it, or bounce and say why. */
  function resolve(held: Grip, to: SpiderMoveDest): void {
    if (held.kind === 'faceDown') {
      refuse(held.key, 'That card is face down — clear the ones on top of it first.');
      return;
    }
    if (!held.liftable) {
      refuse(held.key, spider.describeUnliftable(held.cards));
      return;
    }
    if (moves.some((m) => m.kind === 'move' && sameSource(m.from, held.from) && m.to.index === to.index)) {
      move(held.from, to);
      return;
    }
    refuse(held.key, spider.describeRefusal(view as SpiderView, held.cards, to));
  }

  /** Click to lift; click again to put it down. */
  function tap(next: Grip): void {
    if (next.kind === 'faceDown') {
      refuse(next.key, 'That card is face down — clear the ones on top of it first.');
      return;
    }
    // Spider has no foundations to send a card to — completed runs leave on
    // their own — so a second click on the same grip just lets go, where
    // Klondike and FreeCell would bank it.
    if (grip?.kind === 'cards' && sameSource(grip.from, next.from)) {
      setGrip(null);
      return;
    }
    if (!next.liftable) {
      refuse(next.key, spider.describeUnliftable(next.cards));
      return;
    }
    setGrip(next);
  }

  function handleDragStart(event: DragStartEvent): void {
    const held = event.active.data.current?.grip as Grip | undefined;
    if (held) setGrip(held);
  }

  function handleDragEnd(event: DragEndEvent): void {
    const held = event.active.data.current?.grip as Grip | undefined;
    const to = event.over?.data.current?.to as SpiderMoveDest | undefined;
    setGrip(null);
    // Dropped on open felt: no move, and nothing worth complaining about.
    if (held && to) resolve(held, to);
  }

  function attemptDeal(): void {
    if (view!.canDeal) {
      dealRow();
      return;
    }
    pushToast(
      spider.describeDealRefusal({
        stock: view!.stockCount,
        emptyColumn: view!.blockedByEmptyColumn,
      }),
      'warn',
    );
  }

  const bounceClass = (key: string) => (bouncing === key ? 'card--bounce' : '');
  const dragging = grip?.kind === 'cards' ? grip.cards : null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setGrip(null)}
    >
      <div className="spider" aria-label="Spider table" ref={boardRef}>
        <div className="spider__top">
          {/*
            The stock is a button rather than a disabled control when it cannot
            deal: the reason is a rule people forget, so clicking it should
            explain itself rather than do nothing.
          */}
          <button
            type="button"
            className={`spider__stock ${liftableCount === 0 && view.stockCount > 0 ? 'is-urged' : ''}`}
            onClick={attemptDeal}
            disabled={view.stockCount === 0}
            aria-label={
              view.stockCount === 0
                ? 'The stock is empty'
                : `Deal a row — ${view.dealsRemaining} left`
            }
          >
            {view.stockCount > 0 ? (
              <PlayingCard card={{ id: 'stock', suit: 'S', rank: 'A' }} faceDown />
            ) : (
              <span className="spider__slot">—</span>
            )}
            {view.stockCount > 0 && <span className="spider__count">{view.dealsRemaining}</span>}
          </button>

          <div className="spider__home" aria-label={`${view.completedCount} of ${view.sequencesToWin} runs home`}>
            {Array.from({ length: view.sequencesToWin }, (_unused, i) => {
              const king = view.completed[i];
              const arriving = hiddenHome.has(i);
              return (
                <div key={i} className="spider__homeslot" data-home-slot={i}>
                  {king && !arriving ? (
                    <PlayingCard card={king} />
                  ) : (
                    <span className="spider__slot spider__slot--home">{i + 1}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="spider__tableau" aria-label="Tableau">
          {view.tableau.map((column, index) => (
            <Column
              key={index}
              index={index}
              column={column}
              held={heldSource}
              grip={grip}
              highlight={highlightPlayable}
              showTarget={highlightPlayable && canDrop({ zone: 'tableau', index })}
              canLift={canLift}
              bounceClass={bounceClass}
              hiddenSlots={hiddenSlots}
              onTap={tap}
              onDropInto={(to) => grip && resolve(grip, to)}
            />
          ))}
        </div>

        <div className="spider__status">
          <span className="spider__hint">{hintFor(view, liftableCount, highlightPlayable)}</span>
          <label className="assist">
            <input
              type="checkbox"
              checked={highlightPlayable}
              onChange={(event) => setHighlightPlayable(event.target.checked)}
            />
            <span>Highlight playable cards</span>
          </label>
        </div>
      </div>

      {flights.length > 0 && (
        <div className="flights" aria-hidden="true">
          {flights.map((flight) => (
            <div
              key={flight.key}
              className={`flight flight--spider ${flight.kind === 'home' ? 'flight--home' : ''}`}
              style={
                {
                  '--card-w': `${flight.width}px`,
                  '--from-x': `${flight.fromX}px`,
                  '--from-y': `${flight.fromY}px`,
                  '--from-scale': 1,
                  '--to-x': `${flight.toX}px`,
                  '--to-y': `${flight.toY}px`,
                  animationDelay: `${flight.delay}ms`,
                } as React.CSSProperties
              }
            >
              <PlayingCard card={flight.card} faceDown={flight.faceDown} />
            </div>
          ))}
        </div>
      )}

      <DragOverlay dropAnimation={null}>
        {dragging ? (
          <div className="drag-overlay drag-overlay--solitaire">
            {dragging.map((card, i) => (
              <PlayingCard
                key={card.id}
                card={card}
                style={{ marginTop: i === 0 ? 0 : 'calc(var(--card-w) * -1.09)' }}
              />
            ))}
          </div>
        ) : grip?.kind === 'faceDown' ? (
          <div className="drag-overlay drag-overlay--solitaire">
            <PlayingCard card={{ id: 'drag-down', suit: 'S', rank: 'A' }} faceDown />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/** The line under the board. */
function hintFor(view: SpiderView, liftableCount: number, highlight: boolean): string {
  if (view.blockedByEmptyColumn) return 'Fill the empty column before the stock will deal again.';
  if (liftableCount === 0 && view.stockCount === 0) return 'Nothing stacks and the stock is spent.';
  if (liftableCount === 0) return 'Nothing on the board will move — deal a row.';

  const runs = `${view.completedCount} of ${view.sequencesToWin} runs home`;
  if (highlight) return `${liftableCount} card${liftableCount === 1 ? '' : 's'} can move · ${runs}`;
  return `${runs} · drag a card, or click to lift it`;
}

interface DraggableCardProps {
  id: string;
  grip: Grip;
  className: string;
  style?: React.CSSProperties;
  /**
   * Written out as `data-slot-id`. Every position in a column carries one — a
   * card id where the card is face up, `down-col-row` where it is not — so an
   * animation can find where a card landed without knowing what it is.
   */
  slotId?: string;
  label: string;
  onActivate: (grip: Grip) => void;
  children: React.ReactNode;
}

/**
 * One liftable thing on the table.
 *
 * No transform is applied to the source — the DragOverlay draws the card that
 * follows the cursor, so moving this one as well would drag a second ghost
 * along with it.
 */
function DraggableCard({
  id,
  grip,
  className,
  style,
  slotId,
  label,
  onActivate,
  children,
}: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { grip } });

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${className} ${isDragging ? 'is-dragging' : ''}`}
      style={style}
      data-slot-id={slotId}
      {...listeners}
      {...attributes}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onActivate(grip);
      }}
    >
      {children}
    </button>
  );
}

interface ColumnProps {
  index: number;
  column: SpiderView['tableau'][number];
  held: SpiderMoveSource | null;
  grip: Grip | null;
  highlight: boolean;
  showTarget: boolean;
  canLift: (from: SpiderMoveSource) => boolean;
  bounceClass: (key: string) => string;
  /** Slots whose card is mid-flight, held invisible until the copy lands. */
  hiddenSlots: ReadonlySet<string>;
  onTap: (grip: Grip) => void;
  onDropInto: (to: SpiderMoveDest) => void;
}

function Column({
  index,
  column,
  held,
  grip,
  highlight,
  showTarget,
  canLift,
  bounceClass,
  hiddenSlots,
  onTap,
  onDropInto,
}: ColumnProps) {
  const to: SpiderMoveDest = { zone: 'tableau', index };
  const { setNodeRef } = useDroppable({ id: `drop-tableau-${index}`, data: { to } });

  const bare = column.downCount === 0 && column.up.length === 0;
  const liftedFromHere = grip?.kind === 'cards' && grip.from.index === index;

  return (
    <div
      ref={setNodeRef}
      className="spider__column"
      data-column={index}
      // Clicking the felt of the column a card came out of puts it down rather
      // than refusing a move onto itself.
      onClick={() => grip && !liftedFromHere && onDropInto(to)}
      aria-label={`Column ${index + 1}`}
    >
      {bare && <span className="spider__slot spider__slot--column" />}

      {showTarget && (
        <span
          className="spider__landing"
          style={{ top: offsetFor(column.downCount, column.up.length) }}
          aria-hidden="true"
        />
      )}

      {Array.from({ length: column.downCount }, (_unused, i) => {
        const key = `down:${index}:${i}`;
        return (
          <DraggableCard
            key={`down-${i}`}
            id={`down-${index}-${i}`}
            grip={{ kind: 'faceDown', key }}
            className="spider__stacked"
            style={{
              top: offsetFor(i, null),
              visibility: hiddenSlots.has(`down-${index}-${i}`) ? 'hidden' : undefined,
            }}
            slotId={`down-${index}-${i}`}
            label={`Face-down card in column ${index + 1}`}
            onActivate={onTap}
          >
            <PlayingCard
              card={{ id: `down-${index}-${i}`, suit: 'S', rank: 'A' }}
              faceDown
              className={bounceClass(key)}
            />
          </DraggableCard>
        );
      })}

      {column.up.map((card, i) => {
        // Depth counts from the exposed end of the column upwards.
        const depth = column.up.length - i;
        const from: SpiderMoveSource = { zone: 'tableau', index, depth };
        const run = column.up.slice(i);
        const key = `tableau:${index}:${depth}`;
        const movable = canLift(from);
        const isHeld = held?.index === index && held.depth >= depth;

        return (
          <DraggableCard
            key={card.id}
            id={card.id}
            grip={{ kind: 'cards', from, cards: run, liftable: movable, key }}
            className="spider__stacked spider__stacked--up"
            style={{
              top: offsetFor(column.downCount, i),
              // Held invisible at the exact spot its flight is heading for.
              visibility: hiddenSlots.has(card.id) ? 'hidden' : undefined,
            }}
            slotId={card.id}
            label={`${card.rank}${SUIT_SYMBOLS[card.suit]} in column ${index + 1}`}
            onActivate={(next) => (grip && !liftedFromHere ? onDropInto(to) : onTap(next))}
          >
            <PlayingCard
              card={card}
              className={bounceClass(key)}
              playable={highlight && movable}
              dimmed={highlight && !movable}
              selected={isHeld}
            />
          </DraggableCard>
        );
      })}
    </div>
  );
}

/** Exported for the header readout. */
export function describeSpiderProgress(view: {
  completedCount: number;
  sequencesToWin: number;
  dealsRemaining: number;
}): string {
  const deals =
    view.dealsRemaining === 0
      ? 'stock spent'
      : `${view.dealsRemaining} ${view.dealsRemaining === 1 ? 'deal' : 'deals'} left`;
  return `${view.completedCount}/${view.sequencesToWin} runs · ${deals}`;
}
